import { useEffect, useState, useRef } from 'react';
import {
  getConsent,
  setConsent,
  onOpenPreferencesRequest,
  CONSENT_ACCEPTED,
  CONSENT_DECLINED,
} from '../lib/consent';

const PRIVACY_HREF = '/privacy.html';

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const acceptButtonRef = useRef(null);

  useEffect(() => {
    // Show the banner only if the visitor hasn't answered yet.
    if (!getConsent()) {
      setVisible(true);
    }
    // Re-open when the footer "Cookie Preferences" link fires the event.
    const off = onOpenPreferencesRequest(() => setVisible(true));
    return off;
  }, []);

  useEffect(() => {
    if (!visible) return;

    // Pull initial focus onto the Accept button so keyboard users land in the
    // banner and can act immediately. Delayed one frame so the element is in
    // the DOM before .focus() runs.
    const id = requestAnimationFrame(() => acceptButtonRef.current?.focus());

    // ESC = decline. Standard pattern — pressing Escape on a consent prompt
    // should treat it as a soft "no" rather than dismissing without recording.
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDecline();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleAccept = () => {
    setConsent(CONSENT_ACCEPTED);
    setVisible(false);
  };

  const handleDecline = () => {
    setConsent(CONSENT_DECLINED);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="cookie-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-heading"
      aria-describedby="cookie-consent-desc"
    >
      <div className="cookie-consent-inner">
        <div className="cookie-consent-copy">
          <p id="cookie-consent-heading" className="cookie-consent-heading">
            Cookies, real talk.
          </p>
          <p id="cookie-consent-desc" className="cookie-consent-desc">
            We use cookies for analytics and our live chat. Decline and we'll
            skip both — the site still works. See our{' '}
            <a href={PRIVACY_HREF} className="cookie-consent-link">
              Privacy &amp; Cookie Policy
            </a>{' '}
            for details.
          </p>
        </div>
        <div className="cookie-consent-actions">
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn-decline"
            onClick={handleDecline}
          >
            Decline
          </button>
          <button
            ref={acceptButtonRef}
            type="button"
            className="cookie-consent-btn cookie-consent-btn-accept"
            onClick={handleAccept}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};
